import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import '../css/AdManagement.css';
import { useCampaignList } from '../modules/campaign/hooks/useCampaignList';
import { useFilters } from '../modules/campaign/hooks/useFilters';
import { useSelection } from '../modules/campaign/hooks/useSelection';
import CampaignPageLayout from '../modules/campaign/components/CampaignPageLayout';
import newsReleaseConfig from '../modules/campaign/configs/newsRelease.config';
import { action, extra, submitRefund, saveMemo } from '../modules/campaign/services/campaignService';
import { executeCancel, pointPaymentCancel } from '../modules/wallet/services/walletService';
import { download } from '../modules/campaign/services/downloadService';
import ReportUrlModal from '../component/modal/ReportUrlModal';
import MemoModal from '../modules/campaign/components/MemoModal';
import { useMemoEdit } from '../modules/campaign/hooks/useMemoEdit';
import NotificationModal from '../modules/campaign/components/NotificationModal';
import RefundModal from '../modules/campaign/components/RefundModal';

function NewsReleaseManagement() {
  const filters = useFilters(newsReleaseConfig.filters.default);
  const { items, summary, loading, refresh } = useCampaignList(newsReleaseConfig, filters.filter);
  const selection = useSelection(items, newsReleaseConfig.table.keyField);
  const memoEdit = useMemoEdit({
    saveFn: saveMemo,
    onUpdate: () => {
      refresh();
    },
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedNewsRelease, setSelectedNewsRelease] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [reportMetaId, setReportMetaId] = useState(null);
  const [reportPlaceName, setReportPlaceName] = useState(null);
  const [categories, setCategories] = useState([]);
  const [mediaNames, setMediaNames] = useState([]);
  const [configWithDynamicFilters, setConfigWithDynamicFilters] = useState(newsReleaseConfig);
  const [notificationData, setNotificationData] = useState(null);
  const [refundModalData, setRefundModalData] = useState(null);

  // 카테고리 및 언론사 목록 로드
  useEffect(() => {
    const loadExtraData = async () => {
      try {
        const [categoriesResult, mediaNamesResult] = await Promise.all([
          extra(newsReleaseConfig, 'categories'),
          extra(newsReleaseConfig, 'mediaNames', { type: 'news_release' })
        ]);

        const categoriesList = categoriesResult || [];
        const mediaList = mediaNamesResult?.products || [];
        const uniqueMediaList = [...new Set(mediaList.map(p => ({ mediaName: p.mediaName, adProductId: p.adProductId })))];

        setCategories(categoriesList);
        setMediaNames(uniqueMediaList);

        // 필터 스키마 업데이트
        const updatedSchema = newsReleaseConfig.filters.schema.map(field => {
          if (field.name === 'categoryId') {
            return {
              ...field,
              options: [
                { value: '', label: '전체' },
                ...categoriesList.map(cat => ({ value: cat.id || cat.categoryId, label: cat.name || cat.categoryName }))
              ]
            };
          }
          if (field.name === 'adProductId') {
            return {
              ...field,
              options: [
                { value: '', label: '전체' },
                ...uniqueMediaList.map(media => ({ value: media.adProductId, label: media.mediaName }))
              ]
            };
          }
          return field;
        });

        setConfigWithDynamicFilters({
          ...newsReleaseConfig,
          filters: {
            ...newsReleaseConfig.filters,
            schema: updatedSchema
          }
        });
      } catch (error) {
        console.error('카테고리/언론사 목록 로드 실패:', error);
      }
    };

    loadExtraData();
  }, []);

  const handleRowClick = (row) => {
    setSelectedNewsRelease(row);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedNewsRelease(null);
  };

  const openReportModal = (adCampaignMetaId, placeName) => {
    if (!adCampaignMetaId) {
      alert('캠페인 메타 ID가 없습니다.');
      return;
    }
    setReportMetaId(adCampaignMetaId);
    setReportPlaceName(placeName || null);
  };

  const closeReportModal = () => {
    setReportMetaId(null);
    setReportPlaceName(null);
  };

  const openNotificationModal = (data) => {
    if (!data.adCampaignMetaId) {
      alert('캠페인 메타 ID가 없습니다.');
      return;
    }
    setNotificationData(data);
  };

  const closeNotificationModal = () => {
    setNotificationData(null);
  };

  const handleRefundOpen = (selectedIds, allItems) => {
    const keyField = newsReleaseConfig.table.keyField;
    const selectedItems = allItems
      .filter(item => selectedIds.includes(item[keyField]) && (item.isPaid === 1 || item.isPaid === 2 || item.isRefunded === 1))
      .map(item => ({
        adCampaignMetaId: item.adCampaignMetaId,
        userName: item.userName,
        userId: item.userId,
        paymentType: item.paymentType,
        adPaymentTransactionId: item.adPaymentTransactionId,
        totalAmount: item.totalAmount ?? item.totalCost ?? 0,
        refundedAmount: item.refundedAmount ?? 0,
        remainingAmount: item.remainingAmount ?? item.totalAmount ?? item.totalCost ?? 0,
      }));
    const hasPoint = selectedItems.some(i => i.paymentType === 'point');
    if (hasPoint && selectedItems.length > 1) {
      alert('포인트 결제 건이 포함된 경우 1건씩 환불해주세요.');
      return;
    }
    setRefundModalData({ items: selectedItems });
  };

  const handleRefundSubmit = async (refundItems, reason = '환불 처리') => {
    try {
      const nonPointItems = refundItems.filter(i => i.paymentType !== 'point');
      const pointItems = refundItems.filter(i => i.paymentType === 'point' && i.adPaymentTransactionId);

      if (nonPointItems.length > 0) {
        await submitRefund(newsReleaseConfig, nonPointItems);
      }

      const txGroups = {};
      for (const item of pointItems) {
        const txId = item.adPaymentTransactionId;
        if (!txGroups[txId]) txGroups[txId] = { userId: item.userId, items: [] };
        txGroups[txId].items.push({ adCampaignMetaId: item.adCampaignMetaId, cancelAmount: item.cancelAmount });
      }
      for (const [txId, { userId, items: cancelItems }] of Object.entries(txGroups)) {
        await executeCancel(Number(txId), cancelItems, reason);
      }
      setRefundModalData(null);
      selection.clearSelection();
      await refresh();
      alert('환불 처리가 완료되었습니다.');
    } catch (error) {
      console.error('환불 처리 오류:', error);
      alert(error.message || '환불 처리 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    const handleNotificationClick = (e) => {
      const button = e.target.closest('button[data-action="notification"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute('data-campaign-meta-id'));
        const userId = Number(button.getAttribute('data-user-id'));
        const productName = button.getAttribute('data-product-name') || '';
        const submittedAt = button.getAttribute('data-submitted-at') || '';
        const purchaseAmount = Number(button.getAttribute('data-purchase-amount')) || 0;
        openNotificationModal({ adCampaignMetaId, userId, productName, submittedAt, purchaseAmount });
      }
    };

    document.addEventListener('click', handleNotificationClick, true);

    return () => {
      document.removeEventListener('click', handleNotificationClick, true);
    };
  }, []);

  useEffect(() => {
    const handleMemoClick = (e) => {
      const button = e.target.closest('button[data-action="memo"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute("data-campaign-meta-id"));
        const currentMemo = button.getAttribute("data-memo") || null;
        memoEdit.openModal(adCampaignMetaId, currentMemo || null);
      }
    };
    document.addEventListener("click", handleMemoClick, true);
    return () => document.removeEventListener("click", handleMemoClick, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoEdit.openModal]);

  // rowClick 핸들러를 config에 주입 및 report 컬럼 오버라이드
  const configWithRowClick = {
    ...configWithDynamicFilters,
    table: {
      ...configWithDynamicFilters.table,
      rowClick: {
        enabled: true,
        handler: handleRowClick
      },
      columns: configWithDynamicFilters.table.columns.map(col => {
        if (col.key === 'report') {
          return {
            ...col,
            render: (row) => (
              <button
                type="button"
                className="status-btn submitted"
                onClick={(e) => {
                  e.stopPropagation();
                  openReportModal(row.adCampaignMetaId, row.companyName || '');
                }}
              >
                리포트
              </button>
            )
          };
        }
        return col;
      })
    }
  };

  const handleFilterChange = (name, value) => {
    filters.updateFilter(name, value);
  };

  const handleFilterApply = () => {
    filters.applyFilter();
    selection.clearSelection();
  };

  const handleFilterReset = () => {
    filters.resetFilter();
    selection.clearSelection();
  };

  const handleSelect = (id) => {
    selection.toggleId(id);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      selection.selectAll();
    } else {
      selection.clearSelection();
    }
  };

  const handlePageChange = (page) => {
    filters.setPage(page);
  };

  const handleAction = async (actionType, payload, actionConfig) => {
    try {
      const result = await action(newsReleaseConfig, actionType, payload);
      
      if (result.status === 'success') {
        let message = '처리되었습니다.';
        if (actionType === 'submitted') {
          message = '접수 상태가 변경되었습니다.';
        } else if (actionType === 'paid') {
          message = '결제 완료 처리되었습니다.';
        } else if (actionType === 'complete') {
          message = '처리 상태가 변경되었습니다.';
        } else if (actionType === 'refund') {
          message = '환불 처리되었습니다.';
        } else if (actionType === 'delete') {
          message = '삭제되었습니다.';
        }
        alert(message);
        
        selection.clearSelection();
        await refresh();
      } else {
        alert('처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('액션 처리 오류:', error);
      alert(error.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const handleDownload = async (selectedIds, allItems) => {
    try {
      setIsDownloading(true);
      
      const selectedRows = selectedIds.length > 0
        ? allItems.filter(item => selectedIds.includes(item[newsReleaseConfig.table.keyField]))
        : allItems;

      await download(newsReleaseConfig, selectedRows, allItems);
      alert('다운로드가 완료되었습니다.');
    } catch (error) {
      console.error('다운로드 오류:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const transformedSummary = {
    totalCount: summary.total || summary.totalCount || 0,
    waitingCount: summary.waiting || summary.waitingCount || 0,
    processingCount: summary.processing || summary.processingCount || 0,
    completedCount: summary.completed || summary.completedCount || 0
  };

  return (
    <Layout>
      <CampaignPageLayout
        config={configWithRowClick}
        data={{ items, summary: transformedSummary }}
        filter={filters.filter}
        selectedIds={selection.selectedIds}
        loading={loading || isDownloading}
        handlers={{
          tempFilter: filters.tempFilter,
          onFilterChange: handleFilterChange,
          onFilterApply: handleFilterApply,
          onFilterReset: handleFilterReset,
          onSelect: handleSelect,
          onSelectAll: handleSelectAll,
          onPageChange: handlePageChange,
          onAction: handleAction,
          onDownload: handleDownload,
          onRefundOpen: handleRefundOpen,
        }}
      />
      
      {reportMetaId && (
        <ReportUrlModal
          adCampaignMetaId={reportMetaId}
          placeName={reportPlaceName}
          onClose={closeReportModal}
          onSaved={refresh}
        />
      )}
      
      {/* 상세 정보 팝업 */}
      {isPopupOpen && selectedNewsRelease && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>언론 배포 상세 정보</h3>
              <button className="close-button" onClick={handleClosePopup}>×</button>
            </div>
            <div className="popup-body">
              <div className="info-row">
                <label>신청자:</label>
                <span>{selectedNewsRelease.userName}</span>
              </div>
              <div className="info-row">
                <label>업체명:</label>
                <span>{selectedNewsRelease.companyName || '-'}</span>
              </div>
              <div className="info-row">
                <label>업체URL:</label>
                <span>
                  {selectedNewsRelease.companyUrl ? (
                    <a href={selectedNewsRelease.companyUrl} target="_blank" rel="noopener noreferrer">
                      {selectedNewsRelease.companyUrl}
                    </a>
                  ) : '-'}
                </span>
              </div>
              <div className="info-row">
                <label>언론사명:</label>
                <span>{selectedNewsRelease.mediaName || '-'}</span>
              </div>
              <div className="info-row">
                <label>카테고리:</label>
                <span>{selectedNewsRelease.categoryName || '-'}</span>
              </div>
              <div className="info-row">
                <label>대필여부:</label>
                <span>{selectedNewsRelease.isGhostwriting ? '가능' : '불가능'}</span>
              </div>
              <div className="info-row">
                <label>발행기사수:</label>
                <span>{selectedNewsRelease.articleCount || 0}건</span>
              </div>
              <div className="info-row">
                <label>가격:</label>
                <span>{selectedNewsRelease.totalCost?.toLocaleString()}원</span>
              </div>
              <div className="info-row">
                <label>레퍼런스URL:</label>
                <span>
                  {selectedNewsRelease.referenceUrl ? (
                    <a href={selectedNewsRelease.referenceUrl} target="_blank" rel="noopener noreferrer">
                      {selectedNewsRelease.referenceUrl}
                    </a>
                  ) : '-'}
                </span>
              </div>
              {selectedNewsRelease.guideline && (
                <div className="info-row">
                  <label>가이드라인:</label>
                  <span>{selectedNewsRelease.guideline}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <NotificationModal
        isOpen={notificationData !== null}
        adCampaignMetaId={notificationData?.adCampaignMetaId}
        userId={notificationData?.userId}
        productName={notificationData?.productName}
        submittedAt={notificationData?.submittedAt}
        purchaseAmount={notificationData?.purchaseAmount}
        onClose={closeNotificationModal}
        onSuccess={refresh}
      />
      <RefundModal
        isOpen={refundModalData !== null}
        items={refundModalData?.items || []}
        onClose={() => setRefundModalData(null)}
        onSubmit={handleRefundSubmit}
      />

      <MemoModal
        isOpen={memoEdit.isOpen}
        id={memoEdit.memoData?.id}
        currentMemo={memoEdit.memoData?.currentMemo}
        loading={memoEdit.loading}
        onSave={memoEdit.save}
        onDelete={memoEdit.deleteMemo}
        onClose={memoEdit.closeModal}
      />
    </Layout>
  );
}

export default NewsReleaseManagement;
